import React from 'react';
import Modal from 'react-bootstrap/Modal';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { useDispatch } from 'react-redux';
import { MainActions } from '../../actions/MainActions';
import { strings } from '../../resources/strings';
import ServicesContext from '../../contexts/ServicesContext';
import { OrderForListing } from '../../models/OrderForListing';
import { CurrentState } from '../../models/CurrentState';
import { Button } from '../../common-components/Button/Button';

interface IModalNotifyCustomersToCollectRecipeAndDNPsProps {
  selectedOrders?: OrderForListing[];
  userName?: string;
  close: () => void;
  finish: () => void;
}

const ModalNotifyCustomersToCollectRecipeAndDNPsV2: React.FC<IModalNotifyCustomersToCollectRecipeAndDNPsProps> = ({
  selectedOrders,
  userName,
  close,
  finish
}) => {

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const [notifying, setNotifying] = React.useState<boolean>(false);
  const [cancelNotifying, setCancelNotifying] = React.useState<boolean>(false);
  const [notifyingPercent, setNotifyingPercent] = React.useState<number>(0);

  const notifyingError = React.useRef(0);
  const notifyingErrorServerNotResponding = React.useRef(0);
  const notifyingIndex = React.useRef(0);
  const cancel = React.useRef(false);
  const eligibleOrders = React.useRef<CurrentState[] | null>(null);

  const {
    alertService,
    orderForListingService,
    generateInternalCommunicationsToCollectRecipeByOrderService
  } = React.useContext(ServicesContext);

  const memoBodyRender = React.useMemo(() => {
    if (!notifying) {
      return (
        <div style={{ textAlign: 'justify' }}>
          Se realmente deseja notificar os clientes para coletar receitas e DNPs, clique em "Confimar".
          Caso contrário clique em "Cancelar".
        </div>
      )
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ marginBottom: '10px' }}>Aguarde, notificando...</span>
        <ProgressBar now={notifyingPercent} label={`${notifyingPercent}%`} variant='success' />
      </div>
    );
  }, [notifying, notifyingPercent]);

  const memoFooterRender = React.useMemo(() => {
    if (notifying) {
      return (
        <Button text={strings.Cancel} disabled={cancelNotifying} click={() => cancelNotify()} />
      );
    }

    return (
      <>
        <Button text={strings.Cancel} click={() => close()} marginRight={'2px'} />
        <Button text={strings.Confirm} click={() => confirmNotify()} marginLeft={'2px'} />
      </>
    );
  }, [notifying, cancelNotifying]);

  return (
    <Modal
      show={true}
      onHide={() => close()}
      backdrop="static"
      keyboard={false}

    >
      <Modal.Header>
        <Modal.Title>
          {`${strings.NotifyCustomers} (v2)`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '78vh', overflowY: 'auto', overflowX: 'hidden' }}>
        {memoBodyRender}
      </Modal.Body>
      <Modal.Footer>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {memoFooterRender}
        </div>
      </Modal.Footer>
    </Modal>
  );

  async function confirmNotify() {
    const eligibleList = selectedOrders ? await orderForListingService.getEligibleOrdersToNotifyCollectRecipeAndDNPs(selectedOrders) : [];
    if (!eligibleList || eligibleList.length === 0) {
      close();
      return;
    }

    eligibleOrders.current = [...eligibleList];
    console.log('eligible orders to notify: ', eligibleOrders.current);
    setNotifying(true);
    setCancelNotifying(false);
    notifyingError.current = 0;
    notifyingIndex.current = 0;
    cancel.current = false;
    notifyNextOrder(0);
    mainActions.setNotWait(true);
  }

  function cancelNotify() {
    setCancelNotifying(true);
    cancel.current = true;
    mainActions.setNotWait(false);
  }

  async function notifyNextOrder(orderPosition: number) {
    if (cancelNotifying || cancel.current) {
      setNotifying(false);
      return;
    }

    const orderForListing = selectedOrders && orderPosition < selectedOrders.length ? selectedOrders[orderPosition] : null;

    if (!orderForListing) {
      setNotifyingPercent(100);
      setTimeout(() => finishNotifying(), 1000);
      return;
    }

    const orderEligibleForChange = orderForListing && eligibleOrders.current
      ? eligibleOrders.current.filter(xeo => xeo.orderId === orderForListing.order.id).length > 0
      : false;

    let percent = selectedOrders && selectedOrders.length > 0 ? (orderPosition / selectedOrders.length) * 100 : 0;
    percent = +percent.toFixed(0);

    let notifyError = false;
    let serverResponse = false;
    if (orderEligibleForChange) {
      console.log(`order eligible to notify: ${orderForListing.order.number}`);
      try {
        const result = await generateInternalCommunicationsToCollectRecipeByOrderService.handle({ orderId: orderForListing.order.id });
        if (!result?.value?.result) {
          notifyError = true;
        }
      } catch (error: any) {
        serverResponse = error?.response;
        notifyError = true;
      }
    } else {
      console.log(`order ineligible to notify: ${orderForListing.order.number}`);
    }

    if (notifyError) {
      notifyingError.current = notifyingError.current + 1;

      if (!serverResponse) notifyingErrorServerNotResponding.current = notifyingErrorServerNotResponding.current + 1;
    }

    setNotifyingPercent(percent);

    if (orderEligibleForChange) {
      notifyingIndex.current = notifyingIndex.current + 1;
    }

    notifyNextOrder(orderPosition + 1);
  }

  async function finishNotifying() {
    mainActions.setNotWait(false);
    await alertService.info(strings.ProcessFinishedSuccessfully);
    setNotifying(false);
    finish();
  }
}

export { ModalNotifyCustomersToCollectRecipeAndDNPsV2 };